import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, TemplateRef, inject } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NavigationStart, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { catchError, debounceTime, distinctUntilChanged, firstValueFrom, map, of, Subject, filter, take, takeUntil } from 'rxjs';
import {
  Alert,
  AlertService,
  AlertType,
  ModalComponent,
  ModalResult,
  PaginatorFetcher,
  PaginatorFilters,
  TableComponent,
  TableConfig
} from '@michelin/theme';
import { MatDialog } from '@angular/material/dialog';
import { Dealer } from '../../../../shared/dealer/models/dealer';
import { DealerState } from '../../../../shared/state/DealerState';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { PackListService } from '../../services/pack-list.service';
import { PackListFiltersModel } from '../../models/pack-list-filters.model';
import { PackList, PackListPdfRequest, PackListReport } from '../../models/pack-list.model';
import { PaginatedPackListDetailsResponse, PaginatedPackListResponse } from '../../models/paginated-pack-list.model';
import { FEATURE_FLAGS } from '../../../../shared/constants/constants';
import { FeatureflagsService } from '../../../../shared/services/featureflags.service';
import { ExportModalComponent, ExportModalData } from '../../../../shared/components/export-modal/export-modal.component';
import { ExportColumnDef } from '../../../../shared/models/export-column-def.model';
import { ExportToExcelService } from '../../../../shared/services/export-to-excel.service';
import { PackListPdfContext, PackListPdfExportService } from '../../services/pack-list-pdf-export.service';

@Component({
  selector: 'app-packlists',
  templateUrl: './packlists.component.html',
  styleUrls: ['./packlists.component.scss'],
  standalone: false
})
export class PacklistsComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('customerModal') customerModal: ModalComponent;
  @ViewChild('customerModalContent') customerModalContent: TemplateRef<any>;
  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<PackList>;
  @ViewChild('deleteModal', { static: true }) deleteModal: ModalComponent;
  @ViewChild('deleteConfirmation') deleteContentModal: TemplateRef<any>;

  public fb = inject(FormBuilder);
  public store = inject(Store);
  public translationService = inject(TranslationService);
  public packListService = inject(PackListService);
  public featureflagsService = inject(FeatureflagsService);
  public router = inject(Router);
  public alertService = inject(AlertService);
  public dialog = inject(MatDialog);
  public exportToExcelService = inject(ExportToExcelService);
  public packListPdfExportService = inject(PackListPdfExportService);

  packListFilterForm: FormGroup;
  sourceList: string[] = [];
  statusList = this.getStatusList();
  dealer: Dealer;
  userLogin: string;
  plantId: string;
  productSaveDisabled = true;
  packListNum = '';
  deletePackListId = '';
  pageSizeOptions = [10, 25, 50, 100];
  plants = this.store.selectSnapshot(DealerState.plants);
  sites = this.store.selectSnapshot(DealerState.sites);
  selection: PackList[] = [];
  private paginationState = { pageSize: 10, currentPage: 1 };
  currentPage = 1;
  totalPages = 1;
  isBulkExportInProgress = false;
  isPacklistPdfEnabled = this.featureflagsService.getFeatureFlagValue('isPacklistPdfEnabled');
  private readonly packListExportModalId = 'packlist-bulk-export-modal';
  private readonly paginationStoragePrefix = 'packlists-pagination-';
  private readonly destroy$ = new Subject<void>();
  private nextNavigationUrl = '';
  // ponytail: paced browser downloads; tune batch/delay values if export volume grows.
  private readonly bulkExportBatchSize = 10;
  private readonly bulkExportDelayMs = 250;
  private readonly bulkExportBatchPauseMs = 1000;

  tableConfig: TableConfig<PackList> = {
    columns: {
      packListNumber: {
        cell: (item: PackList) => item.packListNum,
        header: this.translationService.translate('packListNumText'),
        sortable: true
      },
      source: {
        cell: (item: PackList) => item.sourceCode,
        header: this.translationService.translate('sourceText'),
        sortable: true
      },
      destination: {
        cell: (item: PackList) => item.destinationCode,
        header: this.translationService.translate('destinationText'),
        sortable: true
      },
      status: {
        cell: (item: PackList) => item.packListStatus,
        header: this.translationService.translate('statusColumnText'),
        sortable: true
      },
      totalItems: {
        cell: (item: PackList) => item.packListTotalItems.toString(),
        header: this.translationService.translate('totalItemText'),
        sortable: true
      },
      creationDate: {
        cell: (item: PackList) => (item.packListDateCreated ? new Date(item.packListDateCreated).toLocaleDateString() : ''),
        header: this.translationService.translate('createdOnColumnText'),
        sortable: true
      },
      action: {
        cell: 'action',
        header: this.translationService.translate('actionText')
      }
    },
    onRowClick: (elm: PackList) => {
      this.router.navigate(['/home/packlists/', elm.packListPk], {
        queryParams: { packListNum: elm.packListNum, source: elm.sourceCode, destination: elm.destinationCode, notes: elm.notes }
      });
    },
    onSelectionChange: (elms: PackList[]) => {
      this.selection = elms;
    },
    selection: {
      columnKeyForCheckboxLabel: 'packListNum',
      allLabel: 'All'
    },
    storage: {
      type: localStorage,
      options: {
        prefix: this.paginationStoragePrefix,
        paginator: true,
        columns: false,
        tags: false
      }
    },
    paginator: {
      style: 'pageNumber',
      options: {
        nbPagesToShow: 5
      },
      pageSize: this.paginationState.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom'
    }
  };

  constructor() {
    this.packListFilterForm = this.fb.group({
      packListNumber: [''],
      source: [''],
      sourceType: [''],
      status: [''],
      receivedDateStart: [''],
      receivedDateEnd: [''],
      customerCode: [''],
      customerName: [''],
      customerId: ['']
    });
  }

  ngOnInit(): void {
    this.productSaveDisabled = this.featureflagsService.getFeatureFlagValue(FEATURE_FLAGS.IS_DUAL_WRITE_ENABLED);
    this.dealer = this.store.selectSnapshot(DealerState.currentDealer);
    this.userLogin = this.store.selectSnapshot(DealerState.currentUser);
    this.plantId = this.dealer?.id || '';
    this.packListFilterForm.get('customerName').disable();

    this.loadSourceCodes();
    this.restoreFilterState();

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntil(this.destroy$)
      )
      .subscribe((event) => {
        this.nextNavigationUrl = (event as NavigationStart).url;
      });

    this.packListFilterForm.valueChanges.pipe(debounceTime(700), distinctUntilChanged()).subscribe((values) => {
      this.persistFilterState();
      this.tableRef?.patchExtraFilters(values, true);
    });
  }

  ngAfterViewInit(): void {
    // mic-table's paginator resets its visual "current page" indicator to page 1 the first time
    // data length is populated (its internal directive reacts to length changes before it re-checks
    // page index), even though the restored page from storage is correct. Force a page-index toggle
    // once the first page of data has loaded so the button UI re-syncs with the real page.
    this.tableRef?.paginator?.dataChanged.pipe(take(1), takeUntil(this.destroy$)).subscribe(() => {
      setTimeout(() => this.syncPaginatorPageIndicator());
    });
  }

  private syncPaginatorPageIndicator(): void {
    const paginator = this.tableRef?.paginator;
    const targetIndex = paginator?.pageIndex ?? 0;
    if (!paginator || !targetIndex) {
      return;
    }
    const paginatorRefs = [paginator.paginatorRef1, paginator.paginatorRef2].filter(Boolean);
    paginatorRefs.forEach((ref) => {
      ref.pageIndex = 0;
    });
    setTimeout(() => {
      paginatorRefs.forEach((ref) => {
        ref.pageIndex = targetIndex;
      });
    });
  }

  ngOnDestroy(): void {
    const isStillInPackListFlow = this.nextNavigationUrl.startsWith('/home/packlists');
    if (!isStillInPackListFlow) {
      this.packListService.clearPaginationState();
      this.packListService.clearFilterState();
      this.clearPaginationStorage();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  clearSelection(): void {
    this.selection = [];
    this.tableRef?.clearSelection();
    this.packListFilterForm.reset();
    this.persistFilterState();
  }

  openPrintSelectedModal(): void {
    if (!this.selection.length || this.dialog.getDialogById(this.packListExportModalId)) {
      return;
    }

    const data: ExportModalData = {
      columns: this.getExportColumns(),
      fileNamePrefix: 'PackList',
      maxExportRows: 10000,
      excelExportCallback: (selectedColumnNames: Record<string, string>, fileName: string) =>
        this.exportSelectedToExcel(selectedColumnNames, fileName),
      pdfExportCallback: this.isPacklistPdfEnabled ? () => this.exportSelectedToPdf() : undefined
    };

    this.dialog.open(ExportModalComponent, {
      id: this.packListExportModalId,
      data,
      width: '480px'
    });
  }

  openCustomerModal(): void {
    this.customerModal.open({
      id: 'customer-select',
      width: 800,
      title: 'Select Customer',
      content: this.customerModalContent,
      confirm: 'Close'
    });
  }

  onCustomerSelected(customer: any): void {
    if (customer) {
      this.packListFilterForm.patchValue({
        customerCode: customer.code,
        customerName: customer.customerName,
        customerId: customer.customerPk
      });
      this.persistFilterState();
      if (this.customerModal && this.customerModal.dialog) {
        this.customerModal.dialog.closeAll();
      }
    }
  }

  private restoreFilterState(): void {
    const savedFilters = this.packListService.getFilterState();
    if (savedFilters) {
      this.packListFilterForm.patchValue(savedFilters, { emitEvent: false });
    }
  }

  private persistFilterState(): void {
    this.packListService.setFilterState(this.packListFilterForm.getRawValue());
  }

  getPackListFilters(sortBy?: string, sortDir?: string): PackListFiltersModel {
    const formValues = this.packListFilterForm.value;
    let sourceCode = '';
    let sourceId = '';
    let type = '';
    if (formValues.source != null && formValues.source != '') {
      sourceCode = formValues.source.split(' -')[0];
      const siteMatch = this.sites.find((s: any) => s.code === sourceCode);
      if (siteMatch) {
        sourceId = siteMatch.id;
        type = 'site';
      } else {
        const plantMatch = this.plants.find((p: any) => p.code === sourceCode);
        if (plantMatch) sourceId = plantMatch.id;
        type = 'plant';
      }
    }
    return {
      packListNumber: formValues.packListNumber || '',
      source: sourceId,
      sourceType: type,
      status: Array.isArray(formValues.status) ? formValues.status : formValues.status ? [formValues.status] : [],
      customerCode: formValues.customerCode || '',
      customerName: formValues.customerName || '',
      customerId: formValues.customerId || '',
      receivedDateStart: formValues.receivedDateStart ? this.convertToLocalDateTime(formValues.receivedDateStart) : '',
      receivedDateEnd: formValues.receivedDateEnd ? this.convertToLocalDateTime(formValues.receivedDateEnd) : '',
      sortBy: sortBy || 'creationDate',
      sortDir: sortDir || 'desc'
    };
  }

  fetcher: PaginatorFetcher<PackList> = (filters: PaginatorFilters) => {
    // Persist current pagination state
    this.packListService.setPaginationState(filters.limit, filters.page);
    this.currentPage = filters.page;

    let sortBy = 'creationDate';
    let sortDir = 'desc';
    if (filters.sort) {
      const [sortField, sortDirection] = filters.sort.split(':');
      if (sortField && sortDirection) {
        sortBy = sortField;
        sortDir = sortDirection;
      }
    }

    const packListFilters = this.getPackListFilters(sortBy, sortDir);
    return this.packListService.searchPackLists(packListFilters, filters.page, filters.limit).pipe(
      map((response: PaginatedPackListResponse) => {
        if (response && response.error && response.error.length > 0) {
          this.alertService.showAlert({
            type: AlertType.strong,
            content: this.translationService.translate('tryAgainText')
          } as Alert);
          return;
        } else {
          const items: PackList[] = response.data.packLists || [];
          const count = response.data.pagination.totalRecords || 0;
          this.totalPages = Math.max(1, Math.ceil(count / filters.limit));
          return { items, count };
        }
      })
    );
  };

  private clearPaginationStorage(): void {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(this.paginationStoragePrefix)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  }

  private getExportColumns(): ExportColumnDef[] {
    return [
      { key: 'received', label: 'Received', selected: true },
      { key: 'receiveDate', label: 'Received Date', selected: true },
      { key: 'itemNumber', label: 'Item Number', selected: true },
      { key: 'description', label: 'Description', selected: true },
      { key: 'customerCode', label: 'Customer Code', selected: true },
      { key: 'customerName', label: 'Customer Name', selected: true },
      { key: 'workOrderNumber', label: 'Work Order Number', selected: true },
      { key: 'crn', label: 'CRN', selected: true },
      { key: 'lineNumber', label: 'Line #', selected: true },
      { key: 'processCode', label: 'PC', selected: true },
      { key: 'width', label: 'Width', selected: true }
    ];
  }

  private exportSelectedToExcel(selectedColumnNames: Record<string, string>, fileName: string): void {
    const selectedPackLists = [...this.selection];
    if (!selectedPackLists.length) {
      return;
    }

    void this.exportExcelFilesSequentially(selectedPackLists, selectedColumnNames, fileName);
  }

  // Requests are issued one at a time (not via forkJoin) so bulk exports don't fire every detail
  // request concurrently, which overloaded the backend and stalled the whole export before any file saved.
  private async exportExcelFilesSequentially(
    packLists: PackList[],
    selectedColumnNames: Record<string, string>,
    fileName: string
  ): Promise<void> {
    this.isBulkExportInProgress = true;
    let exportedFilesCount = 0;

    try {
      for (let index = 0; index < packLists.length; index++) {
        const packList = packLists[index];
        const details = await firstValueFrom(
          this.packListService.searchPackListDetails(packList.packListPk, 1, 10000).pipe(
            map((response: PaginatedPackListDetailsResponse) => (response?.data?.packListDetails || []) as Record<string, any>[]),
            catchError(() => of([] as Record<string, any>[]))
          )
        );

        if (details.length) {
          this.exportToExcelService.exportToXls(details, selectedColumnNames, this.buildPackListFileName(fileName, packList.packListNum), {
            'Pack List #': packList.packListNum,
            'Source': packList.sourceCode,
            'Destination': packList.destinationCode,
            'Notes': packList.notes ?? ''
          });
          exportedFilesCount++;
        }

        const isLastFile = index === packLists.length - 1;
        if (!isLastFile) {
          const waitMs = (index + 1) % this.bulkExportBatchSize === 0 ? this.bulkExportBatchPauseMs : this.bulkExportDelayMs;
          await this.wait(waitMs);
        }
      }
    } finally {
      this.isBulkExportInProgress = false;
    }

    if (!exportedFilesCount) {
      this.alertService.showAlert({
        type: AlertType.strong,
        content: this.translationService.translate('tryAgainText')
      } as Alert);
    }
  }

  private wait(ms: number): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(resolve, ms);
    });
  }

  private buildPackListFileName(fileName: string, packListNum: string): string {
    const match = fileName.match(/^(.*)-(\d{4}-\d{2}-\d{2}T\d{2}-\d{2}-\d{2})$/);
    if (!match) {
      return `${fileName}-${packListNum}`;
    }
    const [, prefix, timestamp] = match;
    return `${prefix}-${packListNum}-${timestamp}`;
  }

  private exportSelectedToPdf(): void {
    const selectedPackLists = [...this.selection];
    if (!selectedPackLists.length) {
      return;
    }

    void this.exportPdfFilesSequentially(selectedPackLists);
  }

  // Requests are issued one at a time (not via forkJoin) so bulk exports don't fire every detail
  // request concurrently, which overloaded the backend and stalled the whole export before any PDF saved.
  private async exportPdfFilesSequentially(packLists: PackList[]): Promise<void> {
    this.isBulkExportInProgress = true;
    let exportedFilesCount = 0;

    try {
      for (let index = 0; index < packLists.length; index++) {
        const packList = packLists[index];
        const request: PackListPdfRequest = { packListNum: packList.packListNum };
        const report = await firstValueFrom(
          this.packListService.getPackListDetails(request).pipe(catchError(() => of(null as PackListReport | null)))
        );

        if (report) {
          this.packListPdfExportService.generatePackListPdf(
            this.getPackListPdfContext(report),
            report.finishedRetreadPartSummary || [],
            report.repairPartNumberSummary || [],
            report.rejectPartNumberSummary || [],
            report.tableRows || []
          );
          exportedFilesCount++;
        }

        const isLastFile = index === packLists.length - 1;
        if (!isLastFile) {
          await this.wait(this.bulkExportDelayMs);
        }
      }
    } finally {
      this.isBulkExportInProgress = false;
    }

    if (!exportedFilesCount) {
      this.alertService.showAlert({
        type: AlertType.strong,
        content: this.translationService.translate('tryAgainText')
      } as Alert);
    }
  }

  private getPackListPdfContext(report: PackListReport): PackListPdfContext {
    return {
      packListNumber: report.packListNumber,
      userLocationCode: report.userLocationCode,
      destinationType: report.destinationType,
      destinationName: report.destinationName,
      sourceName: report.sourceName,
      sourceType: report.sourceType,
      userLogin: report.userLogin,
      generationDate: report.generationDate,
      totalCasings: report.totalCasings,
      retreadCount: report.retreadCount,
      rejectCount: report.rejectCount
    };
  }

  private loadSourceCodes(): void {
    this.sourceList = Array.from(
      new Set([...this.sites.map((site) => `${site.code} - ${site.name}`), ...this.plants.map((plant) => `${plant.code} - ${plant.name}`)])
    ).sort();
  }

  private getStatusList(): string[] {
    return ['Approved', 'Open', 'Received'];
  }

  /**
   * Converts a JS Date string (e.g. 'Tue Jul 01 2025 00:00:00 GMT+0000') to 'yyyy-MM-dd'T'HH:mm:ss' format for Java LocalDateTime
   */
  private convertToLocalDateTime(dateStr: string): string {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    const pad = (n: number) => (n < 10 ? '0' + n : n);
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(
      date.getMinutes()
    )}:${pad(date.getSeconds())}`;
  }

  showDeleteModal(item: PackList): void {
    this.packListNum = item.packListNum;
    this.deletePackListId = item.packListPk;
    this.deleteModal.open({
      id: item.packListPk,
      width: 500,
      title: 'Delete Pack List',
      content: this.deleteContentModal,
      confirm: {
        text: 'Delete Pack List',
        color: 'warn'
      },
      cancel: this.translationService.translate('cancelLinkText')
    });
  }

  onDeleteModalClosed(result: ModalResult) {
    if (result && result.result) {
      this.packListService.deletePackList(this.deletePackListId).subscribe({
        next: () => {
          this.alertService.showAlert({
            type: AlertType.success,
            content: 'Pack List deleted successfully'
          } as Alert);
          this.tableRef.resetFilters();
        },
        error: () => {
          this.alertService.showAlert({
            type: AlertType.strong,
            content: this.translationService.translate('tryAgainText')
          } as Alert);
        }
      });
    }
  }
}
